import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { createProduct, createTag, updateProduct } from '../../api/business/endpoints';
import type {
  ProductBackorders,
  ProductDetail,
  ProductInput,
  ProductTexts,
  ProductType,
  ProductVisibility,
} from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { classificationInput, classificationOf } from '../../components/classificationDraft';
import type { ClassificationDraft } from '../../components/classificationDraft';
import { FormDialog } from '../../components/FormDialog';
import { formatMoney, formatPercent } from '../../components/format';
import { HtmlTextField } from '../../components/HtmlTextField';
import { LanguageTabs } from '../../components/LanguageTabs';
import { languageName } from '../../components/localisedText';
import { parseMoneyInput } from '../../components/parse';
import { netFromGross, VAT_RATES } from '../../components/prices';
import { draftOf, emptyTexts, inputFrom } from '../../components/productDraft';
import type { DraftTextSetter, ProductDraft } from '../../components/productDraft';
import { PRODUCT_TYPES } from '../../components/productTypes';
import { slugify } from '../../components/slug';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';

import { ProductAvailabilityFields } from './ProductAvailabilityFields';
import { ProductClassificationFields } from './ProductClassificationFields';
import { ProductLegalFields } from './ProductLegalFields';
import { ProductSeoFields } from './ProductSeoFields';

type ProductDialogProps = {
  companyId: string;
  currency: string;
  product: ProductDetail | null;
  onClose: () => void;
  onSaved?: (product: ProductDetail) => void;
};

const VISIBILITIES: Array<ProductVisibility> = ['EVERYWHERE', 'CATALOGUE', 'SEARCH', 'HIDDEN'];
const BACKORDERS: Array<ProductBackorders> = ['DENY', 'ALLOW', 'ALLOW_NOTIFY'];

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack spacing={2}>
    <Divider textAlign="left">
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
    </Divider>
    {children}
  </Stack>
);

/**
 * A product and every property a shop has (R0030 WP-05), in sections: texts per content language, its place in
 * the company's taxonomy (WP-06), its type — standard or a pack (WP-09) — codes, prices with VAT, the legal
 * properties (WP-07), dimensions, display, stock, availability and SEO (WP-07). The form checks what it can; the API checks it all — the EAN
 * digit, the codes and slug being free, the sale against the price — and sanitises the descriptions.
 */
export const ProductDialog = ({ companyId, currency, product, onClose, onSaved }: ProductDialogProps) => {
  const { language, t } = useLanguage();
  const { languages, available } = useContentLanguages();
  const [original] = useState<ProductDraft | null>(() => (product ? draftOf(product, languages) : null));
  const [draft, setDraft] = useState<ProductDraft>(() => original ?? draftOf(null, languages));
  const [active, setActive] = useState(languages[0] ?? 'sk');
  const [problem, setProblem] = useState<string | null>(null);
  const [classifiedAs] = useState<ClassificationDraft | null>(() =>
    product ? classificationOf(product, languages) : null,
  );
  const [classification, setClassification] = useState<ClassificationDraft>(
    () => classifiedAs ?? classificationOf(null, languages),
  );

  const mutation = useAppMutation(
    async ({ input, newTags }: { input: ProductInput; newTags: Array<string> }) => {
      // A tag typed that the company does not have is made first, in the default language (R0030 WP-06).
      const newTagIds: Array<string> = [];

      for (const name of newTags) {
        newTagIds.push((await createTag(companyId, { [languages[0]]: name })).id);
      }

      const placed = classificationInput(classification, classifiedAs, languages, newTagIds);
      const full: ProductInput = { ...input, ...(placed.input ?? {}) };

      return product ? updateProduct(companyId, product.id, full) : createProduct(companyId, full);
    },
    ['products', 'product', 'licence-usage', 'tags', 'categories', 'brands', 'feature-groups'],
    (saved) => {
      onSaved?.(saved);
      onClose();
    },
  );

  const current = languages.includes(active) ? active : languages[0];
  const isDefault = current === languages[0];
  const texts = draft.texts[current] ?? emptyTexts();
  const gross = parseMoneyInput(draft.price);
  const net = gross === undefined ? null : netFromGross(gross, draft.vatRate);

  const set = <TKey extends keyof ProductDraft>(key: TKey, value: ProductDraft[TKey]): void =>
    setDraft((previous) => ({ ...previous, [key]: value }));
  const setTextIn: DraftTextSetter = (code, field, value) =>
    setDraft((previous) => ({
      ...previous,
      texts: { ...previous.texts, [code]: { ...(previous.texts[code] ?? emptyTexts()), [field]: value } },
    }));
  const setText = (field: keyof ProductTexts, value: string): void => setTextIn(current, field, value);

  const submit = (): void => {
    const result = inputFrom(draft, original, languages);

    if (result.problem !== null) {
      setProblem(t(result.problem));

      return;
    }

    const placed = classificationInput(classification, classifiedAs, languages);

    if (placed.problem !== null) {
      setProblem(t(placed.problem));

      return;
    }

    setProblem(null);
    mutation.mutate({ input: result.input, newTags: classification.newTags });
  };

  const number = (key: keyof ProductDraft, label: string) => (
    <TextField
      fullWidth
      label={label}
      value={draft[key] as string}
      inputProps={{ inputMode: 'numeric' }}
      onChange={(e) => set(key, e.target.value as ProductDraft[typeof key])}
    />
  );

  return (
    <FormDialog
      title={product ? t('products.edit') : t('products.create')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={product ? t('common.save') : t('common.create')}
      isBusy={mutation.isPending}
      error={mutation.error}
      problem={problem}
      maxWidth="md"
    >
      <Section title={t('products.section.texts')}>
        {languages.length > 1 ? (
          <LanguageTabs
            languages={languages}
            value={current}
            onChange={setActive}
            missing={(code) => (draft.texts[code]?.name ?? '').trim() === ''}
          />
        ) : null}
        <TextField
          fullWidth
          autoFocus
          required={isDefault}
          label={
            languages.length > 1
              ? `${t('field.productName')} (${languageName(current, available, language)})`
              : t('field.productName')
          }
          helperText={languages.length > 1 && !isDefault ? t('languages.translationHint') : undefined}
          value={texts.name}
          inputProps={{ maxLength: 200 }}
          onChange={(e) => setText('name', e.target.value)}
        />
        <HtmlTextField
          label={t('field.shortDescription')}
          value={texts.shortDescription}
          onChange={(value) => setText('shortDescription', value)}
          maxLength={4000}
          rows={2}
        />
        <HtmlTextField
          label={t('field.description')}
          value={texts.description}
          onChange={(value) => setText('description', value)}
          maxLength={100000}
          rows={5}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            label={t('field.slug')}
            helperText={t('products.slugHint')}
            value={texts.slug}
            inputProps={{ maxLength: 128, style: { fontFamily: 'ui-monospace, monospace' } }}
            onChange={(e) => setText('slug', e.target.value)}
          />
          <Button
            sx={{ mt: 1, whiteSpace: 'nowrap' }}
            disabled={texts.name.trim() === ''}
            onClick={() => setText('slug', slugify(texts.name, current))}
          >
            {t('products.slugGenerate')}
          </Button>
        </Box>
      </Section>

      <Section title={t('products.section.type')}>
        <TextField
          select
          fullWidth
          label={t('field.productType')}
          helperText={t(`productType.hint.${draft.type}`)}
          value={draft.type}
          onChange={(e) => set('type', e.target.value as ProductType)}
        >
          {PRODUCT_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`productType.${type}`)}
            </MenuItem>
          ))}
        </TextField>
      </Section>

      <Section title={t('classification.title')}>
        <ProductClassificationFields
          companyId={companyId}
          languages={languages}
          available={available}
          draft={classification}
          onChange={setClassification}
        />
      </Section>

      <Section title={t('products.section.codes')}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label={t('field.sku')}
              value={draft.sku}
              onChange={(e) => set('sku', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label={t('field.ean')}
              helperText={t('products.eanHint')}
              value={draft.ean}
              inputProps={{ inputMode: 'numeric', maxLength: 14 }}
              onChange={(e) => set('ean', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label={t('field.mpn')}
              value={draft.mpn}
              inputProps={{ maxLength: 64 }}
              onChange={(e) => set('mpn', e.target.value)}
            />
          </Grid>
        </Grid>
      </Section>

      <Section title={t('products.section.prices')}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('field.grossPrice', { currency })}
              helperText={
                net === null
                  ? t('products.grossPriceHint')
                  : t('products.netPrice', { price: formatMoney(net, currency, language) })
              }
              value={draft.price}
              inputProps={{ inputMode: 'decimal' }}
              onChange={(e) => set('price', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              label={t('field.vatRate')}
              value={draft.vatRate}
              onChange={(e) => set('vatRate', Number(e.target.value))}
            >
              {VAT_RATES.map((rate) => (
                <MenuItem key={rate} value={rate}>
                  {formatPercent(rate, language)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label={t('field.salePrice', { currency })}
              helperText={t('products.saleHint')}
              value={draft.salePrice}
              inputProps={{ inputMode: 'decimal' }}
              onChange={(e) => set('salePrice', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              fullWidth
              type="date"
              label={t('field.saleFrom')}
              value={draft.saleFrom}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => set('saleFrom', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              fullWidth
              type="date"
              label={t('field.saleTo')}
              value={draft.saleTo}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => set('saleTo', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('field.costPrice', { currency })}
              helperText={t('products.costHint')}
              value={draft.costPrice}
              inputProps={{ inputMode: 'decimal' }}
              onChange={(e) => set('costPrice', e.target.value)}
            />
          </Grid>
        </Grid>
      </Section>

      <Section title={t('legal.title')}>
        <ProductLegalFields draft={draft} set={set} currency={currency} />
      </Section>

      <Section title={t('products.section.measures')}>
        {draft.type === 'VIRTUAL' ? (
          <Alert severity="info" variant="outlined">
            {t('products.virtualMeasures')}
          </Alert>
        ) : null}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>{number('weightGrams', t('field.weightGrams'))}</Grid>
          <Grid size={{ xs: 6, sm: 3 }}>{number('widthMm', t('field.widthMm'))}</Grid>
          <Grid size={{ xs: 6, sm: 3 }}>{number('heightMm', t('field.heightMm'))}</Grid>
          <Grid size={{ xs: 6, sm: 3 }}>{number('depthMm', t('field.depthMm'))}</Grid>
        </Grid>
      </Section>

      <Section title={t('products.section.display')}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              label={t('field.visibility')}
              value={draft.visibility}
              onChange={(e) => set('visibility', e.target.value as ProductVisibility)}
            >
              {VISIBILITIES.map((visibility) => (
                <MenuItem key={visibility} value={visibility}>
                  {t(`visibility.${visibility}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch checked={draft.featured} onChange={(e) => set('featured', e.target.checked)} />
              }
              label={t('field.featured')}
            />
          </Grid>
        </Grid>
      </Section>

      <Section title={t('products.section.stock')}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              fullWidth
              label={t('field.backorders')}
              value={draft.backorders}
              onChange={(e) => set('backorders', e.target.value as ProductBackorders)}
            >
              {BACKORDERS.map((policy) => (
                <MenuItem key={policy} value={policy}>
                  {t(`backorders.${policy}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{number('lowStockThreshold', t('field.lowStockThreshold'))}</Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{number('minOrderQuantity', t('field.minOrderQuantity'))}</Grid>
        </Grid>
      </Section>

      <Section title={t('availability.title')}>
        <ProductAvailabilityFields
          draft={draft}
          set={set}
          setText={setTextIn}
          languages={languages}
          available={available}
        />
      </Section>

      <Section title={t('seo.title')}>
        <ProductSeoFields draft={draft} setText={setTextIn} languages={languages} available={available} />
      </Section>
    </FormDialog>
  );
};
